import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCents, discountLabel } from '../../utils/introOffer';
import { popButton, pressHandlers } from './FunnelPlans';

// ── Pay what you want ──────────────────────────────────────────────────────
// The `pwyw` arm of the funnel's paywall offer experiment. From the Pro card's
// "Choose your price" pill, a sheet rises over the paywall: "We're so sure
// you'll love Pixley…" and the first month's price, which the parent sets
// with a slider (every whole dollar down from the regular price, so every
// stop ends in .99) or one of four quick picks. The big price rolls to each
// new value, the line under it says what the deal is in words, and the
// button names exactly what will be charged. Confirming hands the cents back
// to the paywall; nothing here talks to the server — the backend prices the
// session from its own table and accepts only a stop it offers itself.
//
// `choose` is the backend's block for the month
// (`funnel_offer_variants.pwyw.plans.pro.choose.monthly`):
//   { stops, min_cents, max_cents, suggested_cents }
// `stops` are the prices on offer in cents, lowest first: one fixed Stripe
// coupon per stop plus the regular price, exactly as the backend will charge
// them. (Older responses without `stops` are read as whole-dollar steps.)
// `valueCents` is the price currently on the card (chosen earlier, or the
// suggested one); `onConfirm(cents)` and `onClose()` are the two ways out.

const prefersReducedMotion = () => {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
};

/** Every price the chooser offers, lowest first: the server's `stops`. */
export const priceStops = ({ stops, min_cents: min, max_cents: max, step_cents: step = 100 } = {}) => {
  if (Array.isArray(stops)) {
    return [...new Set(stops.filter((c) => Number.isInteger(c) && c > 0))].sort((a, b) => a - b);
  }
  const out = [];
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min || !(step > 0)) return out;
  for (let c = max; c >= min; c -= step) out.push(c);
  return out.reverse();
};

/** The four quick picks: three dollars under the suggested price, the
 * suggested price, two over, and the regular price ($4.99 / $7.99 / $9.99 /
 * $14.99 for the live offer) — only stops that exist. */
export const quickPicks = (stops, suggested) => {
  const has = (c) => stops.includes(c);
  const wanted = [suggested - 300, suggested, suggested + 200, stops[stops.length - 1]];
  return [...new Set(wanted.filter(has))].sort((a, b) => a - b);
};

/** What the deal is, in words, for the price on the dial. */
export const dealLine = (cents, { min, max, suggested }) => {
  const label = discountLabel(max, cents);
  if (cents >= max) return 'The regular price. That’s very kind of you.';
  if (cents <= min) return `${label} off · our lowest. Welcome aboard.`;
  if (cents === suggested) return `${label} off · what most families pick`;
  if (cents < suggested) return `${label} off your first month`;
  return `${label} off · thank you`;
};

// The big number rolls from where it was to the new price (eased, ~450ms),
// then lands with a small pop — the same idea as the card's CountdownPrice,
// re-targeted every time the dial moves. Reduced motion: it just changes.
const useRollingCents = (target, ms = 450) => {
  const instant = prefersReducedMotion() || typeof requestAnimationFrame !== 'function';
  const [cents, setCents] = useState(target);
  const [landed, setLanded] = useState(0);
  const fromRef = useRef(target);
  const frameRef = useRef(null);
  useEffect(() => {
    if (instant) {
      setCents(target);
      return undefined;
    }
    const from = fromRef.current;
    if (from === target) return undefined;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (target - from) * eased);
      setCents(value);
      fromRef.current = value;
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
        setLanded((n) => n + 1);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current);
    };
  }, [target, ms, instant]);
  return { cents: instant ? target : cents, landed };
};

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const HeartBadge = () => (
  <div className="fpc-badge" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="26" height="26" fill="#FFFFFF">
      <path d="M12 21s-7.2-4.6-9.4-9.1C1 8.5 3.2 4.8 6.8 4.8c2 0 3.4 1.1 4.2 2.4.8-1.3 2.2-2.4 4.2-2.4 3.6 0 5.8 3.7 4.2 7.1C19.2 16.4 12 21 12 21z" />
    </svg>
    <span className="fpc-badge-spark fpc-badge-spark-a" />
    <span className="fpc-badge-spark fpc-badge-spark-b" />
  </div>
);

const row = (i) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { delay: 0.12 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
});

export const FunnelPriceChooser = ({
  open,
  choose,
  valueCents,
  childName = '',
  onConfirm,
  onClose,
}) => {
  const titleId = useId();
  const sliderId = useId();
  const stops = useMemo(() => priceStops(choose), [choose]);
  const bounds = useMemo(() => ({
    min: stops[0],
    max: stops[stops.length - 1],
    suggested: stops.includes(choose?.suggested_cents) ? choose.suggested_cents : stops[Math.floor(stops.length / 2)],
  }), [stops, choose]);
  const picks = useMemo(() => quickPicks(stops, bounds.suggested), [stops, bounds.suggested]);
  const startAt = stops.includes(valueCents) ? valueCents : bounds.suggested;
  const [cents, setCentsRaw] = useState(startAt);
  // Reopened: start from the price that is on the card now
  useEffect(() => {
    if (open) setCentsRaw(startAt);
  }, [open, startAt]);
  const setCents = (next) => {
    if (stops.includes(next)) setCentsRaw(next);
  };
  const index = Math.max(0, stops.indexOf(cents));
  const { cents: shown, landed } = useRollingCents(cents);
  const sliderRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Escape closes; the page behind does not scroll while the sheet is up;
  // the dial has focus on open so the keyboard works at once
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focus = setTimeout(() => sliderRef.current?.focus({ preventScroll: true }), 260);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      clearTimeout(focus);
    };
  }, [open]);

  if (typeof document === 'undefined' || !stops.length) return null;
  const name = childName.trim();
  const pct = stops.length > 1 ? (index / (stops.length - 1)) * 100 : 100;
  const reduced = prefersReducedMotion();

  return createPortal(
    <>
      <FunnelPriceChooserStyles />
      <AnimatePresence>
        {open && (
          <motion.div
            key="pwyw"
            className="fpc-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.25 } }}
            exit={{ opacity: 0, transition: { duration: 0.2, delay: 0.05 } }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) onClose?.();
            }}
            data-testid="price-chooser"
          >
            <motion.div
              className="fpc-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 64, scale: 0.96 }}
              animate={reduced
                ? { opacity: 1, transition: { duration: 0.2 } }
                : { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 30, mass: 0.9 } }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.98, transition: { duration: 0.18 } }}
            >
              <button type="button" className="fpc-close" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </button>

              <motion.div {...row(0)} className="fpc-head">
                <HeartBadge />
                <span className="fpc-eyebrow">Pay what you want</span>
                <h2 id={titleId} className="fpc-title">
                  We&rsquo;re so sure you&rsquo;ll love Pixley&hellip;
                </h2>
                <p className="fpc-sub">
                  &hellip;that {name ? `${name}’s` : 'the'} first month costs whatever you think it&rsquo;s worth.
                  Pick a price. After that it&rsquo;s {formatCents(bounds.max)}/month, cancel anytime.
                </p>
              </motion.div>

              <motion.div {...row(1)} className="fpc-price-wrap" aria-live="polite">
                <span className="fpc-price-label">Your first month</span>
                <span
                  key={landed}
                  className={`fpc-price${landed && !reduced ? ' is-landed' : ''}`}
                  data-testid="price-chooser-amount"
                >
                  {formatCents(shown)}
                </span>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={dealLine(cents, bounds)}
                    className="fpc-deal"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.22 } }}
                    exit={{ opacity: 0, y: -6, transition: { duration: 0.14 } }}
                  >
                    {dealLine(cents, bounds)}
                  </motion.span>
                </AnimatePresence>
              </motion.div>

              <motion.div {...row(2)} className="fpc-dial">
                <label htmlFor={sliderId} className="fpc-visually-hidden">First month price</label>
                <input
                  ref={sliderRef}
                  id={sliderId}
                  className="fpc-range"
                  type="range"
                  min={0}
                  max={stops.length - 1}
                  step={1}
                  value={index}
                  onChange={(e) => setCents(stops[Number(e.target.value)])}
                  aria-valuemin={bounds.min}
                  aria-valuemax={bounds.max}
                  aria-valuenow={cents}
                  aria-valuetext={`${formatCents(cents)} for the first month`}
                  style={{ '--fill': `${pct}%` }}
                />
                <div className="fpc-dial-ends" aria-hidden="true">
                  <span>Our lowest<br /><strong>{formatCents(bounds.min)}</strong></span>
                  <span>Regular price<br /><strong>{formatCents(bounds.max)}</strong></span>
                </div>
              </motion.div>

              <motion.div
                {...row(3)}
                className="fpc-picks"
                role="group"
                aria-label="Quick picks"
                style={{ gridTemplateColumns: `repeat(${picks.length}, minmax(0, 1fr))` }}
              >
                {picks.map((c) => {
                  const selected = c === cents;
                  const note = c === bounds.suggested ? 'Most pick' : c === bounds.max ? 'Full price' : null;
                  return (
                    <motion.button
                      key={c}
                      type="button"
                      className={`fpc-pick${selected ? ' is-selected' : ''}`}
                      onClick={() => setCents(c)}
                      aria-pressed={selected}
                      animate={{ scale: selected ? 1.05 : 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      <span className="fpc-pick-amount">{formatCents(c)}</span>
                      {note && <span className="fpc-pick-note">{note}</span>}
                    </motion.button>
                  );
                })}
              </motion.div>

              <motion.div {...row(4)} className="fpc-actions">
                <button
                  type="button"
                  className="fpc-confirm"
                  style={popButton(true)}
                  {...pressHandlers(true)}
                  onClick={() => onConfirm?.(cents)}
                >
                  Pay {formatCents(cents)} for my first month
                </button>
                <button type="button" className="fpc-cancel" onClick={onClose}>
                  Never mind
                </button>
                <p className="fpc-fine">
                  Then {formatCents(bounds.max)}/month. Cancel anytime in Settings.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export const FunnelPriceChooserStyles = () => (
  <style>{`
    .fpc-backdrop {
      position: fixed; inset: 0; z-index: 45;
      display: flex; align-items: flex-end; justify-content: center;
      background: rgba(8, 18, 48, 0.58);
      -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
      padding: 0;
    }
    .fpc-sheet {
      position: relative;
      width: 100%; max-width: 100%;
      max-height: min(92dvh, 760px); overflow-y: auto;
      box-sizing: border-box;
      padding: 22px 20px calc(16px + env(safe-area-inset-bottom, 0px));
      border-radius: 30px 30px 0 0;
      background: #FFFFFF;
      box-shadow: 0 -18px 60px rgba(8,18,48,0.35);
      text-align: center;
      font-family: 'Inter', 'Fredoka', sans-serif;
      color: #0B1A33;
    }
    @media (min-width: 561px) {
      .fpc-backdrop { align-items: center; padding: 24px; }
      .fpc-sheet { width: 460px; border-radius: 30px; padding: 30px 30px 26px; box-shadow: 0 30px 80px rgba(8,18,48,0.4); }
    }
    .fpc-close {
      position: absolute; top: 14px; right: 14px;
      width: 36px; height: 36px; border-radius: 50%;
      border: none; background: #F2F6FD; color: #5A6B85;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; -webkit-tap-highlight-color: transparent;
      transition: background 150ms ease, transform 120ms ease;
    }
    .fpc-close:hover { background: #E7EEFA; }
    .fpc-close:active { transform: scale(0.94); }
    .fpc-head { display: flex; flex-direction: column; align-items: center; }
    .fpc-badge {
      position: relative;
      width: 52px; height: 52px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(145deg, #FF7FAB 0%, #FF5C8E 60%, #F0407A 100%);
      box-shadow: 0 10px 24px rgba(255,92,142,0.38), inset 0 1px 0 rgba(255,255,255,0.45);
      margin-bottom: 10px;
      animation: fpcBadgeIn 0.7s cubic-bezier(0.34, 1.4, 0.64, 1) 0.1s both;
    }
    .fpc-badge-spark {
      position: absolute; width: 8px; height: 8px; border-radius: 50%;
      background: #FFC93C; box-shadow: 0 0 0 2px rgba(255,201,60,0.25);
      animation: fpcSpark 1.8s ease-in-out infinite;
    }
    .fpc-badge-spark-a { top: -2px; right: -4px; }
    .fpc-badge-spark-b { bottom: 2px; left: -6px; width: 6px; height: 6px; animation-delay: 0.6s; }
    @keyframes fpcBadgeIn { 0% { transform: scale(0.4) rotate(-12deg); opacity: 0; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
    @keyframes fpcSpark { 0%, 100% { transform: scale(0.7); opacity: 0.5; } 50% { transform: scale(1.15); opacity: 1; } }
    .fpc-eyebrow {
      display: inline-block;
      padding: 5px 12px; border-radius: 999px;
      background: #E8F2FF; color: #0065F8;
      font-family: 'Fredoka', sans-serif; font-size: 11px; font-weight: 700;
      letter-spacing: 0.1em; text-transform: uppercase;
      margin-bottom: 8px;
    }
    .fpc-title {
      margin: 0 0 8px;
      font-family: 'Fredoka', sans-serif; font-size: 26px; font-weight: 700; line-height: 1.1;
      letter-spacing: -0.01em; color: #0B1A33;
      text-wrap: balance;
    }
    .fpc-sub {
      margin: 0 0 14px;
      font-size: 14px; line-height: 1.45; color: #5A6B85;
      text-wrap: balance;
    }
    .fpc-price-wrap {
      display: flex; flex-direction: column; align-items: center;
      padding: 12px 14px 11px;
      border-radius: 22px;
      background: linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%);
      border: 2px solid rgba(0,101,248,0.14);
      box-shadow: 0px 3px 0px rgba(0,101,248,0.10);
      margin-bottom: 10px;
    }
    .fpc-price-label {
      font-family: 'Fredoka', sans-serif; font-size: 13px; font-weight: 700;
      letter-spacing: 0.06em; text-transform: uppercase; color: #5A6B85;
    }
    .fpc-price {
      display: inline-block;
      font-family: 'Fredoka', sans-serif; font-size: 54px; font-weight: 700; line-height: 1;
      letter-spacing: -0.03em; color: #0B1A33;
      font-variant-numeric: tabular-nums;
      margin: 2px 0 4px;
    }
    .fpc-price.is-landed { animation: fpcPriceLand 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
    @keyframes fpcPriceLand { 0% { transform: scale(1); } 45% { transform: scale(1.12); } 100% { transform: scale(1); } }
    .fpc-deal {
      display: block; min-height: 18px;
      font-size: 13.5px; font-weight: 600; color: #0065F8;
    }
    .fpc-dial { padding: 0 4px; margin-bottom: 10px; }
    .fpc-range {
      -webkit-appearance: none; appearance: none;
      width: 100%; height: 32px; margin: 0; padding: 0;
      background: transparent; cursor: pointer;
      --fill: 50%;
    }
    .fpc-range:focus { outline: none; }
    .fpc-range::-webkit-slider-runnable-track {
      height: 10px; border-radius: 999px;
      background: linear-gradient(90deg, #0065F8 0%, #3E8DFD var(--fill), #D7E3F3 var(--fill), #D7E3F3 100%);
      box-shadow: inset 0 1px 2px rgba(11,26,51,0.08);
    }
    .fpc-range::-moz-range-track { height: 10px; border-radius: 999px; background: #D7E3F3; }
    .fpc-range::-moz-range-progress { height: 10px; border-radius: 999px; background: linear-gradient(90deg, #0065F8, #3E8DFD); }
    .fpc-range::-webkit-slider-thumb {
      -webkit-appearance: none; appearance: none;
      width: 30px; height: 30px; border-radius: 50%;
      margin-top: -10px;
      background: #FFFFFF;
      border: 3px solid #0065F8;
      box-shadow: 0 4px 12px rgba(0,101,248,0.35), 0 0 0 0 rgba(0,101,248,0.18);
      transition: box-shadow 160ms ease, transform 120ms ease;
    }
    .fpc-range::-moz-range-thumb {
      width: 24px; height: 24px; border-radius: 50%;
      background: #FFFFFF; border: 3px solid #0065F8;
      box-shadow: 0 4px 12px rgba(0,101,248,0.35);
    }
    .fpc-range:active::-webkit-slider-thumb, .fpc-range:focus-visible::-webkit-slider-thumb {
      box-shadow: 0 4px 12px rgba(0,101,248,0.35), 0 0 0 8px rgba(0,101,248,0.16);
      transform: scale(1.06);
    }
    .fpc-dial-ends {
      display: flex; justify-content: space-between;
      margin-top: 0;
      font-size: 11px; line-height: 1.25; color: #8CA0BC; text-align: left;
    }
    .fpc-dial-ends span:last-child { text-align: right; }
    .fpc-dial-ends strong { font-family: 'Fredoka', sans-serif; font-size: 12.5px; color: #334155; font-weight: 700; }
    .fpc-picks {
      display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px;
      margin-bottom: 14px;
    }
    .fpc-pick {
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
      min-height: 48px; padding: 6px 4px;
      border-radius: 14px;
      background: #FFFFFF; border: 2px solid rgba(11,26,51,0.08);
      box-shadow: 0px 2px 0px rgba(11,26,51,0.05);
      cursor: pointer; -webkit-tap-highlight-color: transparent;
      transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;
    }
    .fpc-pick.is-selected {
      background: linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%);
      border-color: rgba(255,255,255,0.5);
      box-shadow: 0px 3px 0px #073B8E;
    }
    .fpc-pick-amount { font-family: 'Fredoka', sans-serif; font-size: 16px; font-weight: 700; color: #0B1A33; font-variant-numeric: tabular-nums; }
    .fpc-pick.is-selected .fpc-pick-amount { color: #FFFFFF; }
    .fpc-pick-note { font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #8CA0BC; }
    .fpc-pick.is-selected .fpc-pick-note { color: rgba(255,255,255,0.85); }
    .fpc-actions { display: flex; flex-direction: column; align-items: center; }
    .fpc-confirm { font-size: 16px !important; }
    .fpc-cancel {
      margin-top: 6px; padding: 7px 12px;
      border: none; background: none; cursor: pointer;
      font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 700; color: #6B7892;
    }
    .fpc-fine { margin: 2px 0 0; font-size: 11.5px; color: #8CA0BC; }
    .fpc-visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
    @media (max-width: 374px) {
      .fpc-title { font-size: 24px; }
      .fpc-price { font-size: 50px; }
      .fpc-picks { gap: 6px; }
      .fpc-pick-amount { font-size: 14.5px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fpc-badge, .fpc-badge-spark, .fpc-price.is-landed { animation: none; }
    }
  `}</style>
);

export default FunnelPriceChooser;
